import React from 'react';
import { Link } from 'react-router-dom';
import '../legal/legal.css';
import { openCookieSettings } from '../legal/cookieConsent';
import { BrandLogo } from '../legal/illustrations';
import { StoreBadges } from './home/HomeArt';
import { useT } from '../i18n/I18nProvider';

// ─────────────────────────────────────────────────────────────────────────────
// Shared site footer ("bottom menu"). The brand logo lives here now; the main
// row offers the smart "zu MainEvents" entry point (into the app / web app) and
// the Hilfe-Center. The lower strip keeps the legal links (Datenschutz,
// Nutzungsbedingungen, Impressum, Cookie-Richtlinie + settings) and copyright.
// ─────────────────────────────────────────────────────────────────────────────

const LEGAL_NAV = [
    { to: '/datenschutz', key: 'footer.legal.datenschutz' },
    { to: '/nutzungsbedingungen', key: 'footer.legal.nutzungsbedingungen' },
    { to: '/impressum', key: 'footer.legal.impressum' },
    { to: '/cookies', key: 'footer.legal.cookies' },
];

const FOOTER_COLUMNS = [
    {
        titleKey: 'footer.discover.title',
        links: [
            { to: '/events', key: 'nav.discover' },
            { to: '/tickets', key: 'nav.tickets' },
            { to: '/events?category=1', key: 'cat.music' },
            { to: '/events?category=6', key: 'cat.networking' },
        ],
    },
    {
        titleKey: 'footer.organize.title',
        links: [
            { to: '/profile', key: 'nav.profile' },
            { to: '/hilfe#events-erstellen', key: 'footer.organize.myEvents' },
            { to: '/hilfe#events-erstellen', key: 'footer.organize.privateEvents' },
            { to: '/hilfe#events-erstellen', key: 'footer.organize.dashboard' },
        ],
    },
    {
        titleKey: 'footer.support.title',
        links: [
            { to: '/hilfe', key: 'nav.help' },
            { to: '/profil-melden', key: 'footer.support.report' },
            { to: '/profil-loeschen', key: 'footer.support.delete' },
        ],
    },
];

export default function Footer() {
    const { t } = useT();
    const year = new Date().getFullYear();

    return (
        <footer className="se-foot">
            <div className="se-foot-inner se-container">
                <div className="se-foot-brand">
                    <Link to="/" className="se-foot-logo" aria-label="MainEvents">
                        <BrandLogo className="se-brand-logo" />
                    </Link>
                </div>

                <nav className="se-foot-main">
                    <Link to="/events">{t('nav.discover')}</Link>
                    <Link to="/hilfe#events-erstellen">{t('nav.organize')}</Link>
                    <Link to="/tickets">{t('nav.tickets')}</Link>
                    <Link to="/model">{t('nav.venues')}</Link>
                    <Link to="/profile">{t('nav.profile')}</Link>
                    <Link to="/hilfe">{t('nav.help')}</Link>
                </nav>

                <div className="se-foot-columns">
                    {FOOTER_COLUMNS.map((column) => (
                        <section key={column.titleKey} className="se-foot-column">
                            <h3>{t(column.titleKey)}</h3>
                            {column.links.map((link) => (
                                <Link key={`${column.titleKey}-${link.to}-${link.key}`} to={link.to}>
                                    {t(link.key)}
                                </Link>
                            ))}
                        </section>
                    ))}
                </div>

                <div className="se-foot-stores">
                    <span className="se-foot-stores-label">{t('footer.getApp')}</span>
                    <StoreBadges />
                </div>

                <div className="se-foot-legal">
                    {/* Plain anchors with target="_blank": inside the native app's
                        WebView, SPA router navigation stalls on an endless spinner —
                        a new window/tab hands the page to the real browser. */}
                    {LEGAL_NAV.map((n) => (
                        <a key={n.to} href={n.to} target="_blank" rel="noopener noreferrer">
                            {t(n.key)}
                        </a>
                    ))}
                    <button type="button" className="se-link-btn" onClick={openCookieSettings}>
                        {t('footer.cookieSettings')}
                    </button>
                </div>

                <div className="se-foot-copy">{t('footer.copyright', { year })}</div>
            </div>
        </footer>
    );
}
